import { useCrud } from '../../../lib/hooks';

type FormField = {
    id: string | number;
    answer?: {
        value: unknown;
    };
};

type Team = {
    id: string | number;
    name?: string;
};

type User = {
    id?: string | number;
    name?: string;
};

type Participant = {
    id?: string | number;
    name?: string;
    mobile?: string;
    email?: string;
    notes?: string;
    user?: User;
    team?: Team;
    admin?: boolean;
    paymentForm?: string;
    form?: FormField[];
};

export function useParticipantFuncs(): [
    (participant: Participant, categoryId: string | number) => Promise<unknown>,
    (participant: Participant) => Promise<unknown>,
    (participant: Participant) => Promise<unknown>,
] {
    const [, , , , Post, Put, Delete] = useCrud<any>();

    function composeFormData(form: FormField[]): Record<string, unknown> {
        return form.reduce(
            (result: Record<string, unknown>, field: FormField) => {
                result[field.id] = field.answer ? field.answer.value : '';
                return result;
            },
            {} as Record<string, unknown>,
        );
    }

    function composeCreateParams(
        participant: Participant,
    ): Record<string, unknown> {
        const {
            name,
            mobile,
            email,
            notes,
            user,
            team,
            admin,
            paymentForm,
            form,
        } = participant;
        const idTeamExists = !!(team && team.id && team.id !== '0');

        return {
            name,
            mobile,
            email,
            notes,
            idUser: user?.id,
            paymentForm: paymentForm || '',
            idTeam: idTeamExists ? team!.id : null,
            teamName: team && !idTeamExists ? team.name : null,
            admin: idTeamExists ? +!!admin : null,
            form: form ? composeFormData(form) : null,
        };
    }

    // v7 PUT /events/participants/{id} only accepts these fields.
    // Sending idUser/idTeam/teamName/paymentForm yields 400 "is not allowed".
    function composeUpdateParams(
        participant: Participant,
    ): Record<string, unknown> {
        const { name, mobile, email, notes, admin, form, team } = participant;
        const hasTeam = !!(team && team.id && team.id !== '0');

        return {
            name,
            mobile: mobile || undefined,
            email: email || undefined,
            notes: notes || undefined,
            admin: hasTeam ? +!!admin : null,
            form: form ? composeFormData(form) : null,
        };
    }

    function saveParticipant(
        participant: Participant,
        categoryId: string | number,
    ): Promise<unknown> {
        if (participant.id) {
            return Put(
                `/events/participants/${participant.id}`,
                composeUpdateParams(participant),
            );
        }
        return Post(
            `/events/categories/${categoryId}/participants`,
            composeCreateParams(participant),
        );
    }

    function deleteParticipant(participant: Participant): Promise<unknown> {
        return Delete(`/events/participants/${participant.id}`);
    }

    function demoteAdmin(participant: Participant): Promise<unknown> {
        if (!participant.id) {
            return Promise.reject(
                new Error('demoteAdmin requires participant.id'),
            );
        }
        return Put(`/events/participants/${participant.id}`, {
            ...composeUpdateParams(participant),
            admin: 0,
        });
    }

    return [saveParticipant, deleteParticipant, demoteAdmin];
}
